import {
    Add,
    Box,
    Button,
    Chip,
    Clear,
    Delete,
    Divider,
    DropdownContent,
    DropdownMenu,
    DropdownMenuItem,
    DropdownTrigger,
    FilterIcon,
    SearchBar,
    Select,
    TextInput,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { deduceRiskLevelValue } from "components/asset-management-system/assetRiskUtils";
import style from "components/asset-management-system/filter-dropdown.scss";
import { useIsMobileViewport } from "components/navigation/hooks/useIsMobileViewport";
import {
    DISPLAY_TO_FILTER_OPERATOR,
    FilterOperator,
    FilterPathType,
    TEXT_FILTER_OPERATOR_KEYS,
} from "domain/asset-management/assetFilterConstants";
import { AssetPathKey } from "domain/asset-management/assetPathMapping";
import { AssetColumn, AssetFilter, assetService } from "services/asset-management/AssetService";
import { createTranslatorPath } from "services/pathTranslator";

import testIds from "testIds.json";

export const toAssetFilter = (filter: ActiveFilter): AssetFilter => {
    const operator = DISPLAY_TO_FILTER_OPERATOR[filter.operatorKey] ?? DISPLAY_TO_FILTER_OPERATOR["is"];
    return {
        name: filter.apiPath,
        value: filter.value,
        operator,
        pathType: operator === FilterOperator.MATCH ? FilterPathType.STRING : FilterPathType.KEYWORD,
    };
};

export interface ActiveFilter {
    key: string;
    label: string;
    operatorKey: string;
    value: string;
    apiPath: string;
}

interface Props {
    columns: AssetColumn[];
    activeFilters: ActiveFilter[];
    onFiltersChange: (filters: ActiveFilter[]) => void;
    onSaveFilters?: () => void;
    onCreateAssetGroup?: () => void;
}
type Placement = "bottom-end" | "bottom-start";

export const FilterDropdown = (props: Props) => {
    const { t } = useTranslation();
    const isMobileViewport = useIsMobileViewport();
    const translatedPaths = createTranslatorPath(useTranslation, "assetPaths");
    const triggerRef = React.useRef<HTMLDivElement | null>(null);
    const floatingContentObserverRef = React.useRef<MutationObserver | null>(null);

    const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);
    const [selectedColumn, setSelectedColumn] = useState<AssetColumn | undefined>(undefined);
    const [savedFilters, setSavedFilters] = useState<ActiveFilter[]>(props.activeFilters);
    const [filterOperator, setFilterOperator] = useState(TEXT_FILTER_OPERATOR_KEYS[0]);
    const [filterValue, setFilterValue] = useState("");
    const [columnSearchQuery, setColumnSearchQuery] = useState("");
    const [dropdownMenuPlacement, setDropdownMenuPlacement] = useState<Placement>("bottom-end");

    const isDirty = JSON.stringify(props.activeFilters) !== JSON.stringify(savedFilters);

    const handleColumnMenuOpenChange = useRef((isOpen: boolean) => {
        setIsColumnMenuOpen(isOpen);
        if (!isOpen) {
            setFilterOperator(TEXT_FILTER_OPERATOR_KEYS[0]);
            setFilterValue("");
            setSelectedColumn(undefined);
            setColumnSearchQuery("");
        }
    }).current;

    const operators = TEXT_FILTER_OPERATOR_KEYS.map((key) => ({
        value: key,
        label: t(`AssetManagement.filters.operators.${key}`),
    }));

    const showValueInput = filterOperator !== "exists";

    // The backend only accepts the known risk levels, so the value must be picked instead of typed.
    const isRiskLevelSelected = selectedColumn?.apiPath === AssetPathKey.RISK_LEVEL;
    const riskLevelItems = assetService
        .getRiskLevels()
        .map((riskLevel) => ({ value: riskLevel, label: deduceRiskLevelValue(t, riskLevel) }));

    const filteredColumns = props.columns.filter((column) => {
        const columnLabel = translatedPaths(column.apiPath);
        return columnLabel.toLowerCase().includes(columnSearchQuery.toLowerCase());
    });

    const handleApply = (filter: ActiveFilter) => {
        props.onFiltersChange([
            ...props.activeFilters.filter((activeFilter) => activeFilter.key !== filter.key),
            filter,
        ]);
        setSelectedColumn(undefined);
    };

    const updateDropdownPlacement = () => {
        if (!triggerRef.current) {
            return;
        }

        const rect = triggerRef.current.getBoundingClientRect();

        const spaceLeft = rect.left;
        const spaceRight = window.innerWidth - rect.right;

        setDropdownMenuPlacement(spaceRight > spaceLeft ? "bottom-start" : "bottom-end");
    };

    const clampFloatingContentRef = (element: HTMLDivElement | null) => {
        floatingContentObserverRef.current?.disconnect();
        floatingContentObserverRef.current = null;

        const floatingElement = element?.parentElement;
        if (!floatingElement) {
            return;
        }

        const clampNegativeTranslateX = () => {
            const match = floatingElement.style.transform.match(/translate\(([-\d.]+)px,\s*([-\d.]+)px\)/);
            if (!match) {
                return;
            }

            const [, translateX, translateY] = match;
            if (Number(translateX) < 0) {
                floatingElement.style.transform = `translate(0px, ${translateY}px)`;
            }
        };

        clampNegativeTranslateX();

        const observer = new MutationObserver(clampNegativeTranslateX);
        observer.observe(floatingElement, { attributes: true, attributeFilter: ["style"] });
        floatingContentObserverRef.current = observer;
    };

    return (
        <Box
            testId={testIds.common.primaryView.table.filter.itself}
            gap={"Xs"}
            flexWrap={"Wrap"}
            verticalAlignment={"Center"}
            className={style.quickFiltersContainer}
        >
            <Box gap={"Xs"} flexWrap={"Wrap"} verticalAlignment={"Center"} className={style.filterChipContainer}>
                {selectedColumn && !props.activeFilters.some((filter) => filter.key === selectedColumn.key) && (
                    <Chip className={style.filterChip} testId={testIds.common.primaryView.table.filter.chip.itself}>
                        <span>{translatedPaths(selectedColumn.apiPath)}</span>
                    </Chip>
                )}
                {props.activeFilters.map((filter) => (
                    <Chip
                        key={filter.key}
                        className={style.filterChip}
                        testId={testIds.common.primaryView.table.filter.chip.itself}
                        data-type={filter.key.toUpperCase()}
                        onClick={() => {
                            const assetColumn = props.columns.find((column) => column.key === filter.key);
                            if (assetColumn) {
                                updateDropdownPlacement();
                                setSelectedColumn(assetColumn);
                                setIsColumnMenuOpen(true);
                            }
                        }}
                    >
                        <span>
                            {isMobileViewport
                                ? filter.value
                                : t(
                                      filter.operatorKey === "exists"
                                          ? "AssetManagement.filters.displayFormatNoValue"
                                          : "AssetManagement.filters.displayFormat",
                                      {
                                          label: filter.label,
                                          operator: t(
                                              `AssetManagement.filters.operators.${filter.operatorKey}`
                                          ).toLowerCase(),
                                          value: filter.value,
                                      }
                                  )}
                        </span>

                        <span
                            data-testid={testIds.common.primaryView.table.filter.chip.removeButton}
                            onClick={(event) => {
                                event.stopPropagation();
                                props.onFiltersChange(
                                    props.activeFilters.filter((activeFilter) => activeFilter.key !== filter.key)
                                );
                            }}
                        >
                            <Clear size={14} />
                        </span>
                    </Chip>
                ))}
            </Box>

            <Box ref={triggerRef} className={style.filterButtonContainer}>
                <DropdownMenu
                    testId={testIds.common.primaryView.table.filter.menu.itself}
                    position={dropdownMenuPlacement}
                    dropdownOffset={"Xxs"}
                    isDropdownOpen={isColumnMenuOpen}
                    onDropdownOpenChange={handleColumnMenuOpenChange}
                >
                    <DropdownTrigger>
                        <Button
                            variant="Tertiary"
                            testId={testIds.common.primaryView.table.filter.button}
                            datatype="filterBy"
                            icon={<FilterIcon size={18} />}
                            onClick={() => {
                                updateDropdownPlacement();
                                setIsColumnMenuOpen(true);
                            }}
                        >
                            {t("Common.filter")}
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent maxHeight={isMobileViewport ? 350 : undefined}>
                        {!selectedColumn ? (
                            <Box ref={clampFloatingContentRef} columnLayout>
                                <Box paddingLeft={"Xxs"} paddingRight={"Xxs"} paddingTop={"Xxs"} paddingBottom={"Xxs"}>
                                    <SearchBar
                                        placeholder={t("Common.search")}
                                        onSearch={(value) => setColumnSearchQuery(value)}
                                        onClear={() => setColumnSearchQuery("")}
                                        searchOnChange
                                        hideLabelSection
                                        name="columnSearch"
                                    />
                                </Box>
                                {filteredColumns.map((assetColumn) => (
                                    <DropdownMenuItem
                                        key={assetColumn.key}
                                        testId={testIds.common.primaryView.table.filter.menu.item}
                                        data-type={assetColumn.key.toUpperCase()}
                                        onClick={() => setSelectedColumn(assetColumn)}
                                    >
                                        {translatedPaths(assetColumn.apiPath)}
                                    </DropdownMenuItem>
                                ))}
                            </Box>
                        ) : (
                            <Box ref={clampFloatingContentRef} columnLayout>
                                <Box columnLayout gap={"Xs"} padding={"Md"}>
                                    {translatedPaths(selectedColumn.apiPath)}
                                    <Select
                                        placeholder={t(`AssetManagement.filters.operators.${filterOperator}`)}
                                        items={operators}
                                        selectedByDefault={filterOperator}
                                        onSelection={(value) => setFilterOperator(String(value))}
                                        hideLabelSection
                                        name="filterOperator"
                                        closeOnSelection
                                    />
                                    {showValueInput &&
                                        (isRiskLevelSelected ? (
                                            <Select
                                                placeholder={""}
                                                items={riskLevelItems}
                                                selectedByDefault={filterValue}
                                                onSelection={(value) => setFilterValue(String(value))}
                                                hideLabelSection
                                                name="filterValue"
                                                closeOnSelection
                                            />
                                        ) : (
                                            <TextInput
                                                placeholder={t("Common.value")}
                                                value={filterValue}
                                                onChange={(event) => setFilterValue(event.target.value)}
                                                hideLabelSection
                                                name="filterValue"
                                            />
                                        ))}
                                    <Button
                                        variant="Primary"
                                        onClick={() => {
                                            setIsColumnMenuOpen(false);
                                            if (!showValueInput || filterValue.trim()) {
                                                handleApply({
                                                    key: selectedColumn.key,
                                                    label: translatedPaths(selectedColumn.apiPath),
                                                    operatorKey: filterOperator,
                                                    value: filterValue.trim(),
                                                    apiPath: selectedColumn.apiPath,
                                                });
                                            }
                                        }}
                                        disabled={showValueInput && !filterValue.trim()}
                                        small
                                    >
                                        {t("Common.apply")}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </DropdownContent>
                </DropdownMenu>
            </Box>

            {props.activeFilters.length > 0 && (
                <Box gap={"Xs"} verticalAlignment={"Center"} className={style.filterActions}>
                    <Divider direction={"Vertical"} className={classNames(style.dividerHeight, style.hiddenOnMobile)} />
                    <Button
                        variant={"Tertiary"}
                        testId={testIds.common.primaryView.table.filter.saveButton}
                        disabled={!isDirty}
                        onClick={() => {
                            props.onSaveFilters?.();
                            setSavedFilters(props.activeFilters);
                        }}
                        className={style.hiddenOnMobile}
                    >
                        {t("Common.saveFilters")}
                    </Button>
                    {props.onCreateAssetGroup && (
                        <>
                            <Divider
                                direction={"Vertical"}
                                className={classNames(style.dividerHeight, style.hiddenOnMobile)}
                            />
                            <Button
                                variant={"Tertiary"}
                                onClick={props.onCreateAssetGroup}
                                icon={<Add size={18} />}
                                testId={testIds.common.primaryView.table.filter.createGroupButton}
                                className={style.hiddenOnMobile}
                            >
                                {t("AssetManagement.createAssetGroup")}
                            </Button>
                        </>
                    )}
                    <Divider direction={"Vertical"} className={classNames(style.dividerHeight, style.hiddenOnMobile)} />
                    <Button
                        variant={"TertiaryDelete"}
                        onClick={() => props.onFiltersChange([])}
                        icon={<Delete size={18} />}
                        testId={testIds.common.primaryView.table.filter.clearButton}
                    >
                        {t("Common.clearFilters")}
                    </Button>
                </Box>
            )}
        </Box>
    );
};
